import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isEmpty, isNil } from 'lodash';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import moment from 'moment';

import { ComponentTierName } from '@/constants/enums';
import { LeakageMock } from '@/constants/LeakageMock';
import epaxios from '@/utils/epaxios';
import { checkTierPermission, waitFor } from '@/utils/util-functions';

import RootStore from './RootStore';
import { permissions } from '@/constants';

dayjs.extend(utc);

export const emptyValueSpaced = '- -';
export const emptyValue = '--';

type NextPeriodicShutdown = { startDate: Date; endDate: Date };

export const getFormattedPeriod = (startDate, endDate, format = { day: 'Do', month: 'MMMM' }) => {
  const momentStartDate = moment.utc(startDate);
  const momentEndDate = moment.utc(endDate);
  let endDateFormat = `MMM ${format.day}, YYYY`;
  let startDateFormat = `MMM ${format.day}, YYYY`;
  if (momentStartDate.year() === momentEndDate.year()) {
    if (momentStartDate.month() === momentEndDate.month()) {
      startDateFormat = `${format.month} DD`;
      endDateFormat = 'DD, YYYY';
    } else {
      startDateFormat = `MMM ${format.day}`;
      endDateFormat = `MMM ${format.day}, YYYY`;
    }
  }
  return `${momentStartDate.format(startDateFormat)} - ${momentEndDate.format(endDateFormat)}`;
};

export interface LeakageOverview {
  totalFlow: number;
  leakRateFlow: number;
  previousLeakRateFlow: number;
  leakRatePercent: number;
  totalKw: number;
  kwLeakRate: number;
  leakageWasted: LeakageSection;
  actionTaken: LeakageSection;
  lastMeasureDate: Date;
  nextPeriodicShutdown: NextPeriodicShutdown;
}

export interface LeakageMeasurementLog {
  startDate: Date;
  endDate: Date;
  averagePressureBar?: number;
  averageFlowM3Min?: number;
  totalFlowM3Min?: number;
  leakRatePercent?: number;
  leakRate?: number;
  id: string;
  status: LeakDetectionPeriodStatus;
}

export interface LeakDetectionPeriod {
  id: string;

  stationId: string;

  startDate: Date;

  endDate: Date;

  createdUserId: string;

  createdUserEmail: string;

  expertUserId?: string;

  expertAnalysisDate?: Date;

  status: LeakDetectionPeriodStatus;

  rejectReason?: string;

  dateCreated: Date;

  dateUpdated?: Date;

  leakedEntityId: string;

  averageFlowM3Min: number;

  averagePressureBar: number;

  leakRate: number;

  totalFlowM3Min: number;

  totalPowerKw: number;
}

export enum LeakDetectionPeriodStatus {
  Draft = 'draft',
  Published = 'published',
  Rejected = 'rejected',
}

interface EntitiesLeakageData {
  entityName: string;
  productionFlowM3Min: number;
  productionPercentage: number;
  wasteFlowM3Min: number;
  wastePercentage: number;
}

export interface LeakageStatistics {
  totalFlowM3Min: number;
  totalProductionFlowM3Min: number;
  totalWasteFlowM3Min: number;
  entitiesLeakageData: EntitiesLeakageData[];
}

export default class LeakageStore {
  rootStore: typeof RootStore;
  @observable loadingFlags = {
    overview: false,
    measurementLog: false,
    statistics: false,
    createPeriodicShutdown: false,
    deletePeriodicShutdown: false,
    updatePeriodicShutdown: false,
  };
  @observable lastLeakage: LastLeakage;
  @observable leakageWasted: LeakageSection;
  @observable leakageSavings: LeakageSection;
  @observable lastMeasureDate: Date;
  @observable nextPeriodicShutdown: NextPeriodicShutdown;
  @observable measurementLog: MeasurementLog[];
  @observable excludeFutureShutdowns = false;
  @observable statistics: Statistics;
  @observable leakDetectionPeriod: LeakDetectionPeriod;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.excludeFutureShutdowns,
      () => {
        this.getMeasurementLog();
      },
    );
  }

  @computed get isLoading(): boolean {
    return Object.values(this.loadingFlags).some(Boolean);
  }

  @action getOverview = async () => {
    this.loadingFlags.overview = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (!stationId) {
        console.warn('No station is selected, skipping leakageStore.getOverview');
        return;
      }
      await waitFor(() => this.rootStore.stationStore.isLoading === false);
      let data;
      if (checkTierPermission(ComponentTierName.Leakage, this.rootStore.stationStore)) {
        data = LeakageMock;
      } else {
        data = (await epaxios.get(`value-dashboard/leakage/station/${stationId}/overview`))?.data;
      }
      this.lastLeakage = new LastLeakage(
        data?.totalFlow,
        data?.leakRateFlow,
        data?.previousLeakRateFlow,
        data?.leakRatePercent,
        data?.totalKw,
        data?.kwLeakRate,
      );
      this.leakageWasted = new LeakageSection(data?.leakageWasted);
      this.leakageSavings = new LeakageSection(data?.actionTaken);
      this.lastMeasureDate = data?.lastMeasureDate;
      this.nextPeriodicShutdown = data?.nextPeriodicShutdown;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Leakage Overview: ${e}`));
    } finally {
      this.loadingFlags.overview = false;
    }
  };

  @action getLeakDetectionPeriod = async (leakageId: string) => {
    this.loadingFlags.measurementLog = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (!stationId) {
        console.warn('No station is selected, skipping leakageStore.getLeakDetectionPeriod');
        return;
      }
      const { data } = await epaxios.get(`value-dashboard/leakage/station/${stationId}/measurement-log/${leakageId}`);
      this.leakDetectionPeriod = data;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Leak Audit Data: ${e}`));
    } finally {
      this.loadingFlags.measurementLog = false;
    }
  };

  @action updateAndPublishLeakDetectionPeriod = async (payload: {
    id: string;
    actualLeakRateFlowM3Min: number;
    startDate: string;
    endDate: string;
    rejectReason?: string;
    status: LeakDetectionPeriodStatus;
  }) => {
    this.loadingFlags.measurementLog = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (!stationId) {
        console.warn('No station is selected, skipping leakageStore.updateAndPublishLeakDetectionPeriod');
        return;
      }
      await epaxios.put(`value-dashboard/leakage/station/${stationId}/measurement-log/${payload.id}`, {
        actualLeakRateFlowM3Min: payload.actualLeakRateFlowM3Min,
        startDate: payload.startDate,
        endDate: payload.endDate,
        rejectReason: payload.rejectReason,
        status: payload.status,
      });
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating and publishing Leak Detection Period: ${e}`));
    } finally {
      this.loadingFlags.measurementLog = false;
    }
  };
  @action getMeasurementLog = async () => {
    this.loadingFlags.measurementLog = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (!stationId) {
        console.warn('No station is selected, skipping leakageStore.getMeasurementLog');
        return;
      }
      const {
        data,
      }: {
        data: LeakDetectionPeriod[];
      } = this.rootStore.permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_ALL_LEAKAGES)
        ? await epaxios.get(`value-dashboard/leakage/station/${stationId}/measurement-log/all`)
        : await epaxios.get(
            `value-dashboard/leakage/station/${stationId}/measurement-log?excludeFutureShutdowns=false`,
          );
      this.measurementLog = data?.map(measurementLogEntry => new MeasurementLog(measurementLogEntry));
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Leakage Measurement Log: ${e}`));
    } finally {
      this.loadingFlags.measurementLog = false;
    }
  };

  @action getStatistics = async () => {
    this.loadingFlags.statistics = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (!stationId) {
        console.warn('No station is selected, skipping leakageStore.getStatistics');
        return;
      }
      const { data } = await epaxios.get(`value-dashboard/leakage/station/${stationId}/statistics`);
      this.statistics = new Statistics(data);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Leakage Statistics: ${e}`));
      this.statistics = null;
    } finally {
      this.loadingFlags.statistics = false;
    }
  };

  @action createPeriodicShutdown = async dates => {
    this.loadingFlags.createPeriodicShutdown = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const payload = {
        startDate: dayjs(dates.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        endDate: dayjs(dates.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      };
      await epaxios.post(`value-dashboard/leakage/station/${stationId}/measurement-log`, payload);
    } catch (e) {
      Sentry.captureException(new Error(`Error when adding Leakage Periodic Shutdown: ${e}`));
    } finally {
      this.loadingFlags.createPeriodicShutdown = false;
    }
  };

  @action deletePeriodicShutdown = async id => {
    this.loadingFlags.deletePeriodicShutdown = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.delete(`value-dashboard/leakage/station/${stationId}/measurement-log/${id}`);
      await this.getMeasurementLog();
    } catch (e) {
      Sentry.captureException(new Error(`Error when deleting Leakage Periodic Shutdown: ${e}`));
    } finally {
      this.loadingFlags.deletePeriodicShutdown = false;
    }
  };

  @action updatePeriodicShutdown = async (id: string, dates: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs }) => {
    this.loadingFlags.updatePeriodicShutdown = true;
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const payload = {
        startDate: dayjs(dates.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        endDate: dayjs(dates.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      };
      await epaxios.put(`value-dashboard/leakage/station/${stationId}/measurement-log/${id}/dates`, payload);
      await this.getMeasurementLog();
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating Leakage Periodic Shutdown: ${e}`));
      throw e;
    } finally {
      this.loadingFlags.updatePeriodicShutdown = false;
    }
  };

  @action setExcludeFutureShutdowns = (excludeFutureShutdowns: boolean) => {
    this.excludeFutureShutdowns = excludeFutureShutdowns;
  };

  @computed get lastMeasureDateFormatted() {
    return this.lastMeasureDate ? moment(this.lastMeasureDate).format('ll') : emptyValue;
  }

  @computed get formattedNextPeriodicShutdown() {
    if (isEmpty(this.nextPeriodicShutdown)) return emptyValueSpaced;
    return getFormattedPeriod(this.nextPeriodicShutdown.startDate, this.nextPeriodicShutdown.endDate);
  }
}

class LastLeakage {
  @observable totalFlow: number;
  @observable leakRateFlow: number;
  @observable previousLeakRateFlow: number;
  @observable leakRatePercent: number;
  @observable totalKw: number;
  @observable kwLeakRate: number;

  constructor(
    totalFlow: number,
    leakRateFlow: number,
    previousLeakRateFlow: number,
    leakRatePercent: number,
    totalKw: number,
    kwLeakRate: number,
  ) {
    makeObservable(this);
    this.totalFlow = totalFlow;
    this.leakRateFlow = leakRateFlow;
    this.previousLeakRateFlow = previousLeakRateFlow;
    this.leakRatePercent = leakRatePercent;
    this.totalKw = totalKw;
    this.kwLeakRate = kwLeakRate;
  }
}

class LeakageSection {
  @observable yearToDate: LeakageItem;
  @observable lifetime: LeakageItem;

  constructor(leakage: Partial<LeakageSection>) {
    makeObservable(this);
    this.yearToDate = new LeakageItem(leakage?.yearToDate);
    this.lifetime = new LeakageItem(leakage?.lifetime);
  }

  @computed get hasValues() {
    return this.yearToDate.hasValues || this.lifetime.hasValues;
  }
}

export class LeakageItem {
  @observable savingValue?: number;
  @observable savingCurrency: string;
  @observable energyKwh?: number;
  @observable savingCo2?: number;

  constructor(leakageItem: LeakageItem) {
    makeObservable(this);
    this.energyKwh = leakageItem?.energyKwh;
    this.savingValue = leakageItem?.savingValue;
    this.savingCurrency = leakageItem?.savingCurrency;
    this.savingCo2 = leakageItem?.savingCo2;
  }

  @computed get hasValues() {
    return [this.savingValue, this.energyKwh, this.savingCo2].some(Boolean);
  }
}

export class MeasurementLog {
  @observable startDate: Date;
  @observable endDate: Date;
  @observable pressure?: number;
  @observable leakFlow?: number;
  @observable totalFlow?: number;
  @observable leakRatePercent?: number;
  @observable leakRate?: number;
  @observable key: string;
  @observable status: LeakDetectionPeriodStatus;

  constructor(measurementLog: LeakageMeasurementLog) {
    makeObservable(this);
    this.key = measurementLog?.id;
    this.startDate = measurementLog?.startDate;
    this.endDate = measurementLog?.endDate;
    this.pressure = measurementLog?.averagePressureBar;
    this.leakFlow = measurementLog?.averageFlowM3Min;
    this.totalFlow = measurementLog?.totalFlowM3Min;
    this.leakRatePercent = measurementLog?.leakRatePercent;
    this.leakRate = measurementLog?.leakRate;
    this.status = measurementLog?.status;
  }

  @computed get isEmptyEvent() {
    return (
      this.startDate &&
      this.endDate &&
      [this.pressure, this.leakFlow, this.totalFlow, this.leakRatePercent, this.leakRate].every(isNil)
    );
  }

  @computed get leakagePercentDuringShutdown() {
    return (this.leakRate / this.leakFlow) * 100 || null;
  }

  @computed get isFutureEvent() {
    return moment(this.startDate).diff(moment()) > 0;
  }

  @computed get isInProgress() {
    return moment(this.startDate).isBefore(moment()) && moment(this.endDate).isAfter(moment());
  }

  @computed get isCompletedEvent() {
    return moment(this.endDate).isBefore(moment());
  }
}

export class Statistics {
  @observable totalFlow: number;
  @observable totalProductionFlow: number;
  @observable totalWasteFlow: number;
  @observable productionSlices: ProductionSlice[];

  constructor(statistics: LeakageStatistics) {
    this.totalFlow = statistics.totalFlowM3Min;
    this.totalProductionFlow = statistics.totalProductionFlowM3Min;
    this.totalWasteFlow = statistics.totalWasteFlowM3Min;
    this.productionSlices = statistics.entitiesLeakageData?.map(
      entityLeakageData => new ProductionSlice(entityLeakageData),
    );
  }
}

export class ProductionSlice {
  @observable name: string;
  @observable production: number;
  @observable productionPercentage: number;
  @observable waste: number;
  @observable wastePercentage: number;

  constructor(productionSlice: EntitiesLeakageData) {
    this.name = productionSlice.entityName;
    this.production = productionSlice.productionFlowM3Min;
    this.productionPercentage = productionSlice.productionPercentage;
    this.waste = productionSlice.wasteFlowM3Min;
    this.wastePercentage = productionSlice.wastePercentage;
  }
}
